import React, { useState, useRef } from 'react';
import { apiClient } from '../../api/client';
import { matchKrioPhrase } from '../../chatbot/krio-phrases';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT } from '../legacy-ball/table-management/designTokens';

/**
 * ConciergeBar — Collapsible inline chat bar for the AI concierge.
 * Collapsed: thin tappable bar "💬 Ask KDU Concierge"
 * Expanded: input field + send button + response area
 */
export const ConciergeBar: React.FC = () => {
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSend = async () => {
    if (!query.trim() || loading) return;
    setLoading(true);
    setResponse('');

    // Check client-side Krio phrase dictionary first (instant response)
    const krioMatch = matchKrioPhrase(query.trim());
    if (krioMatch) {
      setResponse(krioMatch);
      setLoading(false);
      return;
    }

    try {
      const result = await apiClient<{ answer: string }>('/chatbot/ask', {
        method: 'POST',
        body: { question: query.trim() },
      });
      setResponse(result.answer || 'No response.');
    } catch {
      setResponse('Sorry, something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!expanded) {
    return (
      <button
        style={styles.collapsedBar}
        onClick={() => {
          setExpanded(true);
          setTimeout(() => inputRef.current?.focus(), 100);
        }}
        aria-label="Open AI Concierge"
      >
        💬 Ask KDU Concierge
      </button>
    );
  }

  return (
    <div style={styles.expandedContainer}>
      <div style={styles.inputRow}>
        <input
          ref={inputRef}
          style={styles.input}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
          placeholder="Ask me anything..."
          disabled={loading}
        />
        <button
          style={styles.sendBtn}
          onClick={handleSend}
          disabled={loading || !query.trim()}
          aria-label="Send message"
        >
          {loading ? '…' : '→'}
        </button>
        <button
          style={styles.closeBtn}
          onClick={() => { setExpanded(false); setResponse(''); setQuery(''); }}
          aria-label="Close concierge"
        >
          ✕
        </button>
      </div>
      {response && (
        <div style={styles.responseArea}>
          <div style={styles.responseText} dangerouslySetInnerHTML={{ __html: response }} />
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  collapsedBar: {
    width: '100%',
    padding: '8px 16px',
    backgroundColor: '#f0f7f2',
    border: 'none',
    borderTop: `1px solid ${BORDER_LIGHT}`,
    fontSize: '0.8rem',
    fontWeight: 500,
    color: PRIMARY,
    cursor: 'pointer',
    textAlign: 'center' as const,
  },
  expandedContainer: {
    width: '100%',
    padding: '8px 12px',
    backgroundColor: '#f0f7f2',
    borderTop: `1px solid ${BORDER_LIGHT}`,
  },
  inputRow: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
  },
  input: {
    flex: 1,
    padding: '8px 10px',
    fontSize: '0.85rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  sendBtn: {
    width: '34px',
    height: '34px',
    borderRadius: '6px',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    fontSize: '1rem',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeBtn: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: 'transparent',
    border: 'none',
    color: TEXT_MUTED,
    fontSize: '0.9rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  responseArea: {
    marginTop: '8px',
    padding: '8px 10px',
    backgroundColor: '#fff',
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
    maxHeight: '120px',
    overflowY: 'auto' as const,
  },
  responseText: {
    margin: 0,
    fontSize: '0.82rem',
    color: TEXT_DARK,
    lineHeight: 1.4,
  },
};
